(() => {
if (typeof window === 'undefined') {
    return;
}
if (window.__rideGCRScriptLoaded) {
    return;
}
window.__rideGCRScriptLoaded = true;

// DOM Content Loaded
document.addEventListener('DOMContentLoaded', function() {
    ensureFixedElements();
    startPortalObserver();
    // Initialize all functionality
    initNavigation();
    initLanguagePopup();
    initHeroReveals();
    initHeroSlider();
    initHeroLoader();
    initFleetCarousel();
    initCartShowcase();
    initProductLineup();
    initCoverageMap();
    initScrollEffects();
    initFormHandling();
    initAnimations();
    initSmoothScrolling();
    initLazyLoading();
});

// Navigation Functionality
let headerPortalNode = null;
let sideMenuPortalNode = null;
let navCleanup = null;
let portalObserver = null;
let heroDotsPortalNode = null;
let destroyHeroSlider = null;
let destroyFleetCarousel = null;

const syncHeroProgressPosition = () => {
    const header = document.querySelector('.rr-header');
    if (!header || !document?.documentElement) return;
    const top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--hero-progress-top', `${top}px`);
};

const ensureFixedElements = () => {
    if (typeof document === 'undefined') return;
    const header = document.querySelector('.rr-header');
    if (header && header.parentElement !== document.body) {
        document.body.appendChild(header);
        headerPortalNode = header;
    }
    const sideMenu = document.getElementById('sidemenu');
    if (sideMenu && sideMenu.parentElement !== document.body) {
        document.body.appendChild(sideMenu);
        sideMenuPortalNode = sideMenu;
    }
    const dotsContainer = document.querySelector('[data-hero-dots="primary"]');
    if (dotsContainer && dotsContainer.parentElement !== document.body) {
        document.body.appendChild(dotsContainer);
        heroDotsPortalNode = dotsContainer;
    }
    syncHeroProgressPosition();
};

const startPortalObserver = () => {
    if (portalObserver || typeof MutationObserver === 'undefined') return;
    portalObserver = new MutationObserver(() => ensureFixedElements());
    portalObserver.observe(document.body, { childList: true, subtree: true });
};

function initNavigation() {
    ensureFixedElements();
    const header = document.querySelector('.rr-header');
    const sideMenu = document.getElementById('sidemenu');
    const headerToggle = header?.querySelector('.rr-header__menu');

    if (header && sideMenu && headerToggle) {
        if (header.parentElement !== document.body) {
            document.body.appendChild(header);
            headerPortalNode = header;
        }
        if (sideMenu.parentElement !== document.body) {
            document.body.appendChild(sideMenu);
            sideMenuPortalNode = sideMenu;
        }
        if (navCleanup) {
            navCleanup();
            navCleanup = null;
        }
        if (header.dataset.navReady === 'true') {
            return;
        }
        header.dataset.navReady = 'true';

        const closeTriggers = sideMenu.querySelectorAll('[data-menu-close], .side-menu__backdrop');
        const sideMenuPanel = sideMenu.querySelector('.side-menu__panel');
        const focusableSelectors = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
        let lastFocused = null;

        const isMenuOpen = () => sideMenu.getAttribute('aria-hidden') === 'false';
        let lastScrollY = window.scrollY;
        const hideThreshold = 180;

        const handleHeaderScroll = () => {
            const currentY = window.scrollY;
            const scrollingDown = currentY > lastScrollY;
            const shouldHide = currentY > hideThreshold && scrollingDown && !isMenuOpen();
            header.classList.toggle('scrolled', currentY > 20 || isMenuOpen());
            if (header.classList.contains('is-hidden')) {
                header.classList.remove('is-hidden');
            }
            syncHeroProgressPosition();
            lastScrollY = currentY <= 0 ? 0 : currentY;
        };

        const setMenuState = (open) => {
            sideMenu.setAttribute('aria-hidden', open ? 'false' : 'true');
            document.body.classList.toggle('side-menu-open', open);
            document.body.style.overflow = open ? 'hidden' : '';
            headerToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) {
                header.classList.add('scrolled');
                header.classList.remove('is-hidden');
            } else {
                handleHeaderScroll();
            }
            syncHeroProgressPosition();

            if (open) {
                lastFocused = document.activeElement;
                const focusable = Array.from(sideMenu.querySelectorAll(focusableSelectors));
                (focusable[0] || sideMenu).focus();
            } else if (lastFocused) {
                lastFocused.focus();
            }
        };

        const toggleMenu = (event) => {
            if (event) event.preventDefault();
            setMenuState(!isMenuOpen());
        };

        headerToggle.addEventListener('click', toggleMenu);
        closeTriggers.forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                setMenuState(false);
            });
        });

        const keydownHandler = (event) => {
            if (event.key === 'Escape' && isMenuOpen()) {
                setMenuState(false);
            }

            if (event.key === 'Tab' && isMenuOpen()) {
                const focusable = Array.from(sideMenu.querySelectorAll(focusableSelectors));
                if (!focusable.length) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', keydownHandler);

        const outsidePointerHandler = (event) => {
            if (!isMenuOpen()) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (sideMenuPanel?.contains(target)) return;
            if (headerToggle.contains(target)) return;
            setMenuState(false);
        };
        document.addEventListener('pointerdown', outsidePointerHandler);

        if (window.location.hash === '#menu') {
            setMenuState(true);
        }

        handleHeaderScroll();
        const throttledScroll = throttle(handleHeaderScroll, 80);
        const throttledResize = throttle(syncHeroProgressPosition, 120);
        window.addEventListener('scroll', throttledScroll);
        window.addEventListener('resize', throttledResize);
        navCleanup = () => {
            window.removeEventListener('scroll', throttledScroll);
            window.removeEventListener('resize', throttledResize);
            document.removeEventListener('keydown', keydownHandler);
            document.removeEventListener('pointerdown', outsidePointerHandler);
        };
        return;
    }
    
    const navToggle = document.querySelector('.nav-toggle');
    const navMenu = document.querySelector('.nav-menu');
    const navbar = document.querySelector('.navbar');
    
    // Mobile menu toggle (legacy navigation)
    if (navToggle && navMenu) {
        navToggle.addEventListener('click', function() {
            navMenu.classList.toggle('active');
            
            // Animate hamburger menu
            const spans = navToggle.querySelectorAll('span');
            spans.forEach((span, index) => {
                if (navMenu.classList.contains('active')) {
                    if (index === 0) span.style.transform = 'rotate(45deg) translate(5px, 5px)';
                    if (index === 1) span.style.opacity = '0';
                    if (index === 2) span.style.transform = 'rotate(-45deg) translate(7px, -6px)';
                } else {
                    span.style.transform = 'none';
                    span.style.opacity = '1';
                }
            });
        });

        const navLinks = navMenu.querySelectorAll('a');
        navLinks.forEach(link => {
            link.addEventListener('click', () => {
                if (navMenu.classList.contains('active')) {
                    navMenu.classList.remove('active');
                    const spans = navToggle.querySelectorAll('span');
                    spans.forEach(span => {
                        span.style.transform = 'none';
                        span.style.opacity = '1';
                    });
                }
            });
        });
    }
    
    // Navbar scroll effect
    if (navbar) {
        let lastScrollTop = 0;
        window.addEventListener('scroll', function() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            if (scrollTop > 100) {
                navbar.style.background = 'rgba(255, 255, 255, 0.98)';
                navbar.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.1)';
            } else {
                navbar.style.background = 'rgba(255, 255, 255, 0.95)';
                navbar.style.boxShadow = 'none';
            }
            
            // Hide/show navbar on scroll
            if (scrollTop > lastScrollTop && scrollTop > 200) {
                navbar.style.transform = 'translateY(-100%)';
            } else {
                navbar.style.transform = 'translateY(0)';
            }
            
            lastScrollTop = scrollTop;
        });
    }
}

function createLanguagePopupFromSelector(sourceSelect) {
    if (!sourceSelect || !sourceSelect.options || sourceSelect.options.length < 2) return null;

    const popup = document.createElement('div');
    popup.className = 'gcr-language-popup';
    popup.setAttribute('data-language-popup', '');
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-labelledby', 'GcrLanguagePopupTitle');

    const optionsHtml = Array.from(sourceSelect.options)
        .map((option) => {
            const label = (option.textContent || option.value || '').trim();
            const code = (option.value || '').trim();
            if (!code) return '';
            const isActive = option.selected ? ' is-active' : '';
            return `
              <button type="button" class="gcr-language-popup__choice${isActive}" data-language-choice="${code}">
                <span class="gcr-language-popup__choice-name">${label}</span>
                <span class="gcr-language-popup__choice-code">${code.toUpperCase()}</span>
              </button>
            `;
        })
        .join('');

    popup.innerHTML = `
      <button class="gcr-language-popup__backdrop" type="button" data-language-popup-close aria-label="Close language selector"></button>
      <div class="gcr-language-popup__panel">
        <button class="gcr-language-popup__close" type="button" data-language-popup-close aria-label="Close language selector">×</button>
        <p class="gcr-language-popup__kicker">WELCOME · BIENVENIDO</p>
        <h2 class="gcr-language-popup__title" id="GcrLanguagePopupTitle">Choose your language</h2>
        <p class="gcr-language-popup__subtitle">Selecciona el idioma que prefieres para navegar la web.</p>
        <div class="gcr-language-popup__choices" role="group" aria-label="Language options">
          ${optionsHtml}
        </div>
        <button class="gcr-language-popup__skip" type="button" data-language-popup-close>
          Continue with current language
        </button>
      </div>
    `;

    document.body.appendChild(popup);
    return popup;
}

function initLanguagePopup() {
    const sourceSelect = document.querySelector('#HeaderLanguage')
        || document.querySelector('#SideMenuLanguage')
        || document.querySelector('.rr-language-select');

    let popup = document.querySelector('[data-language-popup]');
    if (!popup) {
        popup = createLanguagePopupFromSelector(sourceSelect);
    }
    if (!popup || popup.dataset.popupReady === 'true') return;

    popup.dataset.popupReady = 'true';
    const STORAGE_KEY = 'gcr-language-popup-closed-v3';
    const closeButtons = Array.from(popup.querySelectorAll('[data-language-popup-close]'));
    const choiceButtons = Array.from(popup.querySelectorAll('[data-language-choice]'));
    let isOpen = false;

    const closePopup = (persist = true) => {
        if (!isOpen) return;
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('language-popup-open');
        isOpen = false;
        if (persist) {
            try {
                localStorage.setItem(STORAGE_KEY, '1');
            } catch (error) {}
        }
    };

    const openPopup = () => {
        if (popup.parentElement !== document.body) {
            document.body.appendChild(popup);
        }
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('language-popup-open');
        isOpen = true;
    };

    let shouldOpen = true;
    try {
        shouldOpen = localStorage.getItem(STORAGE_KEY) !== '1';
    } catch (error) {}
    if (window.location.search.includes('lang-popup=1')) {
        shouldOpen = true;
    }

    if (!shouldOpen) {
        popup.setAttribute('aria-hidden', 'true');
        return;
    }

    const openPopupAfterHeroLoader = () => {
        const start = Date.now();
        const maxWaitMs = 2800;

        const checkLoader = () => {
            const loader = document.querySelector('[data-hero-loader]');
            if (!loader) {
                openPopup();
                return;
            }

            if (loader.classList.contains('is-loaded') || loader.dataset.state === 'settled') {
                setTimeout(() => openPopup(), 120);
                return;
            }

            if (Date.now() - start >= maxWaitMs) {
                openPopup();
                return;
            }

            requestAnimationFrame(checkLoader);
        };

        checkLoader();
    };

    openPopupAfterHeroLoader();

    closeButtons.forEach((button) => {
        button.addEventListener('click', () => closePopup(true));
    });

    choiceButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const code = button.getAttribute('data-language-choice');
            if (!code) {
                closePopup(true);
                return;
            }
            try {
                localStorage.setItem(STORAGE_KEY, '1');
            } catch (error) {}

            const targetSelect = document.querySelector('#HeaderLanguage')
                || document.querySelector('#SideMenuLanguage')
                || document.querySelector('.rr-language-select');
            if (!targetSelect) {
                closePopup(true);
                return;
            }

            targetSelect.value = code;
            const targetForm = targetSelect.closest('form');
            if (targetForm) {
                targetForm.submit();
                return;
            }
            closePopup(true);
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen) {
            closePopup(true);
        }
    });
}

// Rolls-Royce style hero reveals
function initHeroReveals() {
    const heroContents = Array.from(document.querySelectorAll('.hero__content')).filter(
        (content) => !content.closest('.hero-slider')
    );
    if (!heroContents.length) return;

    const prefersReduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReduceMotion) {
        heroContents.forEach(content => content.classList.add('is-visible'));
        return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                obs.unobserve(entry.target);
            }
        });
    }, { threshold: 0.35 });

    heroContents.forEach(content => observer.observe(content));
}

// Hero slider scroll snapping + dots
function initHeroSlider() {
    ensureFixedElements();
    if (destroyHeroSlider) {
        destroyHeroSlider();
        destroyHeroSlider = null;
    }

    const slider = document.querySelector('.hero-slider');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('.hero.slide'));
    if (!slides.length) return;

    const dotsContainers = Array.from(slider.querySelectorAll('[data-hero-dots]'));
    const primaryOutside = document.querySelector('[data-hero-dots="primary"]');
    if (primaryOutside && !dotsContainers.includes(primaryOutside)) {
        dotsContainers.push(primaryOutside);
    }
    if (!dotsContainers.length) return;

    const primaryContainer = dotsContainers.find((container) => container.dataset.heroDots === 'primary') || dotsContainers[0];
    if (primaryContainer.dataset.heroDots === 'primary' && primaryContainer.parentElement !== document.body) {
        document.body.appendChild(primaryContainer);
        heroDotsPortalNode = primaryContainer;
    }

    const containerDotsMap = new Map();
    dotsContainers.forEach((container) => {
        const dotList = Array.from(container.querySelectorAll('.dot'));
        dotList.forEach((dot, idx) => {
            dot.dataset.heroDotIndex = idx;
        });
        containerDotsMap.set(container, dotList);
    });

    const referenceDots = containerDotsMap.get(primaryContainer);
    if (!referenceDots || !referenceDots.length) return;

    let isSnapping = false;
    const menu = document.getElementById('sidemenu');
    const isMenuOpen = () => menu && menu.getAttribute('aria-hidden') === 'false';
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    let heroVideoPlaybackEnabled = document.readyState === 'complete';

    const clampIndex = (index) => Math.max(0, Math.min(slides.length - 1, index));

    const primeVideoForAutoplay = (video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('autoplay', '');
        video.setAttribute('loop', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
    };

    const setHeroVideoVisualState = (video, isReady) => {
        const media = video.closest('.hero__bg-media');
        if (!media) return;
        media.classList.toggle('is-video-ready', isReady);
        media.classList.toggle('is-video-loading', !isReady);
    };

    const primeVideoFallbackVisual = (video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        const media = video.closest('.hero__bg-media');
        if (!media) return;

        const poster = video.getAttribute('poster');
        if (poster) {
            media.style.setProperty('--hero-video-fallback-image', `url("${poster}")`);
            media.classList.add('has-video-fallback');
        } else {
            media.classList.remove('has-video-fallback');
            media.style.removeProperty('--hero-video-fallback-image');
        }

        const isReady = video.readyState >= 2;
        setHeroVideoVisualState(video, isReady);

        if (!isReady && video.dataset.heroFallbackBound !== 'true') {
            const onReadyFrame = () => setHeroVideoVisualState(video, true);
            video.addEventListener('loadeddata', onReadyFrame);
            video.addEventListener('playing', onReadyFrame);
            video.dataset.heroFallbackBound = 'true';
        }
    };

    const shouldPlayVideoInViewport = (video) => {
        const isMobileLayer = Boolean(video.closest('.hero__bg-media--mobile'));
        return mobileMediaQuery.matches ? isMobileLayer : !isMobileLayer;
    };

    const attemptPlay = (video) => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }
    };

    const syncHeroBackgroundVideos = (activeIndex) => {
        if (!heroVideoPlaybackEnabled) {
            slides.forEach((slide) => {
                const slideVideos = Array.from(slide.querySelectorAll('video.bg-video'));
                slideVideos.forEach((video) => video.pause());
            });
            return;
        }

        const targetIndex = clampIndex(activeIndex);
        slides.forEach((slide, idx) => {
            const slideVideos = Array.from(slide.querySelectorAll('video.bg-video'));
            slideVideos.forEach((video) => {
                primeVideoForAutoplay(video);
                primeVideoFallbackVisual(video);
                const shouldPlay = idx === targetIndex && shouldPlayVideoInViewport(video);

                if (!shouldPlay) {
                    video.pause();
                    return;
                }

                if (video.readyState < 2) {
                    const onReady = () => {
                        video.removeEventListener('loadeddata', onReady);
                        attemptPlay(video);
                    };
                    video.addEventListener('loadeddata', onReady);
                    video.load();
                }
                attemptPlay(video);
            });
        });
    };

    const setActiveDot = (index) => {
        containerDotsMap.forEach((dotsList, container) => {
            dotsList.forEach((dot, idx) => {
                const isActive = idx === index;
                dot.classList.toggle('is-active', isActive);
                dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
                dot.setAttribute('tabindex', isActive ? '0' : '-1');
            });
            container.style.setProperty('--hero-progress-total', `${Math.max(1, dotsList.length)}`);
            container.style.setProperty('--hero-progress-index', `${Math.max(1, index + 1)}`);
        });
    };

    let activeSlideIndex = -1;

    const revealSlide = (index, { force = false } = {}) => {
        if (!slides[index]) return;
        if (!force && index === activeSlideIndex) return;
        activeSlideIndex = index;
        slides.forEach((slide, idx) => {
            const content = slide.querySelector('.hero__content');
            if (!content) return;
            if (idx === index) {
                content.classList.remove('is-visible');
                void content.offsetWidth;
                content.classList.add('is-visible');
            } else {
                content.classList.remove('is-visible');
            }
        });
    };

    const getVisibleIndex = () => {
        let maxFraction = -1;
        let visibleIndex = 0;
        const viewport = window.innerHeight || document.documentElement.clientHeight;

        slides.forEach((slide, idx) => {
            const rect = slide.getBoundingClientRect();
            const visible = Math.max(0, Math.min(rect.bottom, viewport) - Math.max(rect.top, 0));
            const fraction = visible / Math.max(1, Math.min(viewport, rect.height));
            if (fraction > maxFraction) {
                maxFraction = fraction;
                visibleIndex = idx;
            }
        });

        return visibleIndex;
    };

    const setActiveSlide = (index, options) => {
        const targetIdx = clampIndex(index);
        setActiveDot(targetIdx);
        slides.forEach((slide, idx) => {
            slide.classList.toggle('is-active', idx === targetIdx);
        });
        revealSlide(targetIdx, options);
        syncHeroBackgroundVideos(targetIdx);
    };

    const scrollToIndex = (index) => {
        const targetIndex = clampIndex(index);
        if (isSnapping || !slides[targetIndex]) return;
        isSnapping = true;
        slides[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSlide(targetIndex);
        setTimeout(() => {
            isSnapping = false;
        }, 900);
    };

    setActiveSlide(getVisibleIndex(), { force: true });

    const clickHandler = (event) => {
        const targetDot = event.target.closest('.dot');
        if (!targetDot) return;
        const idx = Number(targetDot.dataset.heroDotIndex);
        if (Number.isNaN(idx)) return;
        event.preventDefault();
        scrollToIndex(idx);
    };
    const listeners = [];
    const addListener = (target, type, handler, options) => {
        if (!target) return;
        target.addEventListener(type, handler, options);
        listeners.push(() => target.removeEventListener(type, handler, options));
    };

    const enableHeroVideoPlayback = () => {
        if (heroVideoPlaybackEnabled) return;
        heroVideoPlaybackEnabled = true;
        syncHeroBackgroundVideos(getVisibleIndex());
    };
    if (!heroVideoPlaybackEnabled) {
        addListener(window, 'load', enableHeroVideoPlayback, { once: true });
    }

    dotsContainers.forEach((container) => addListener(container, 'click', clickHandler));

    const wheelHandler = (event) => {
        if (isMenuOpen()) return;
        if (Math.abs(event.deltaY) < 2) return;

        const current = getVisibleIndex();
        const maxInteractiveIndex = Math.min(1, slides.length - 1);
        if (current > maxInteractiveIndex) return;
        const currentSlide = slides[current];
        if (currentSlide?.dataset?.heroFreeScroll === 'true') return;

        const canAdvance = event.deltaY > 0 && current < maxInteractiveIndex;
        const canGoBack = event.deltaY < 0 && current > 0;

        // Allow normal page scroll outside hero bounds (first/last slide).
        if (!canAdvance && !canGoBack) return;

        event.preventDefault();
        if (canAdvance) {
            scrollToIndex(current + 1);
        } else if (canGoBack) {
            scrollToIndex(current - 1);
        }
    };
    addListener(slider, 'wheel', wheelHandler, { passive: false });

    let touchStartY = null;
    const touchStartHandler = (event) => {
        touchStartY = event.touches[0]?.clientY ?? null;
    };
    const touchEndHandler = (event) => {
        if (isMenuOpen() || touchStartY == null) return;
        const endY = event.changedTouches[0]?.clientY ?? touchStartY;
        const delta = endY - touchStartY;
        if (Math.abs(delta) < 10) {
            touchStartY = null;
            return;
        }

        const current = getVisibleIndex();
        const maxInteractiveIndex = Math.min(1, slides.length - 1);
        if (current > maxInteractiveIndex) {
            touchStartY = null;
            return;
        }
        const currentSlide = slides[current];
        if (currentSlide?.dataset?.heroFreeScroll === 'true') {
            touchStartY = null;
            return;
        }

        if (delta < 0 && current < maxInteractiveIndex) {
            scrollToIndex(current + 1);
        } else if (delta > 0 && current > 0) {
            scrollToIndex(current - 1);
        }
        touchStartY = null;
    };

    addListener(slider, 'touchstart', touchStartHandler, { passive: true });
    addListener(slider, 'touchend', touchEndHandler, { passive: true });

    const handleViewportChange = () => {
        syncHeroBackgroundVideos(getVisibleIndex());
    };
    if (typeof mobileMediaQuery.addEventListener === 'function') {
        mobileMediaQuery.addEventListener('change', handleViewportChange);
        listeners.push(() => mobileMediaQuery.removeEventListener('change', handleViewportChange));
    } else if (typeof mobileMediaQuery.addListener === 'function') {
        mobileMediaQuery.addListener(handleViewportChange);
        listeners.push(() => mobileMediaQuery.removeListener(handleViewportChange));
    }

    addListener(document, 'visibilitychange', () => {
        if (!document.hidden) {
            syncHeroBackgroundVideos(getVisibleIndex());
        }
    });

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const idx = slides.indexOf(entry.target);
                if (idx !== -1) {
                    setActiveSlide(idx);
                }
            }
        });
    }, { threshold: 0.6 });

    slides.forEach(slide => observer.observe(slide));

    destroyHeroSlider = () => {
        listeners.forEach((remove) => remove());
        observer.disconnect();
    };
}

function initHeroLoader() {
    const loader = document.querySelector('[data-hero-loader]');
    if (!loader) return;

    const MIN_LOADER_MS = 650;
    const MAX_LOADER_MS = 1800;

    const settleLoader = () => {
        if (loader.dataset.state === 'settled') return;
        loader.dataset.state = 'settled';
        requestAnimationFrame(() => loader.classList.add('is-loaded'));
        setTimeout(() => loader.remove(), 900);
    };

    setTimeout(settleLoader, MIN_LOADER_MS);
    setTimeout(settleLoader, MAX_LOADER_MS);
}

const HERO_SECTION_SELECTOR = '.hero-slider';
const HEADER_SELECTOR = '.rr-header';
const CART_SECTION_SELECTOR = '.cart-showcase';
const LINEUP_SECTION_SELECTOR = '.gcr-lineup';

function isHeroSliderSection(target) {
    return Boolean(target && typeof target.querySelector === 'function' && target.querySelector(HERO_SECTION_SELECTOR));
}

function isRideHeaderSection(target) {
    return Boolean(target && typeof target.querySelector === 'function' && target.querySelector(HEADER_SELECTOR));
}

function isCartShowcaseSection(target) {
    return Boolean(target && typeof target.querySelector === 'function' && target.querySelector(CART_SECTION_SELECTOR));
}

function isProductLineupSection(target) {
    return Boolean(target && typeof target.querySelector === 'function' && target.querySelector(LINEUP_SECTION_SELECTOR));
}

document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event?.target;
    if (isHeroSliderSection(sectionRoot)) {
        initHeroSlider();
        initHeroLoader();
        initFleetCarousel();
    }
    if (isRideHeaderSection(sectionRoot)) {
        initNavigation();
        initLanguagePopup();
    }
    if (isCartShowcaseSection(sectionRoot)) {
        initCartShowcase();
    }
    if (isProductLineupSection(sectionRoot)) {
        initProductLineup();
    }
});

document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event?.target;
    if (isHeroSliderSection(sectionRoot)) {
        if (typeof destroyHeroSlider === 'function') {
            destroyHeroSlider();
            destroyHeroSlider = null;
        }
        if (typeof destroyFleetCarousel === 'function') {
            destroyFleetCarousel();
            destroyFleetCarousel = null;
        }
        if (heroDotsPortalNode) {
            heroDotsPortalNode.remove();
            heroDotsPortalNode = null;
        }
    }
    if (isRideHeaderSection(sectionRoot)) {
        if (navCleanup) {
            navCleanup();
            navCleanup = null;
        }
        if (headerPortalNode) {
            headerPortalNode.remove();
            headerPortalNode = null;
        }
        if (sideMenuPortalNode) {
            sideMenuPortalNode.remove();
            sideMenuPortalNode = null;
        }
    }
    if (isCartShowcaseSection(sectionRoot)) {
        initCartShowcase();
    }
});

// Fleet carousel helper (prev/next buttons + dots)
function initFleetCarousel() {
    if (destroyFleetCarousel) {
        destroyFleetCarousel();
        destroyFleetCarousel = null;
    }

    const track = document.getElementById('fleet-track');
    const cards = track ? Array.from(track.querySelectorAll('.fleet-card')) : [];
    const prevBtn = document.querySelector('[data-fleet-prev]');
    const nextBtn = document.querySelector('[data-fleet-next]');
    const dots = Array.from(document.querySelectorAll('.fleet__dot'));
    if (!track || !cards.length) return;

    const listeners = [];
    const addListener = (target, type, handler, options) => {
        if (!target) return;
        target.addEventListener(type, handler, options);
        listeners.push(() => target.removeEventListener(type, handler, options));
    };

    let pageOffsets = [0];
    let activePageIndex = 0;
    let dotClickHandlers = [];

    const setActivePage = (index) => {
        const maxIndex = Math.max(0, pageOffsets.length - 1);
        activePageIndex = Math.max(0, Math.min(maxIndex, index));
        dots.forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === activePageIndex);
            const isVisible = idx < pageOffsets.length;
            dot.style.display = isVisible ? '' : 'none';
            dot.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        });
    };

    const scrollToPage = (index) => {
        const maxIndex = Math.max(0, pageOffsets.length - 1);
        const targetIndex = Math.max(0, Math.min(maxIndex, index));
        const targetOffset = pageOffsets[targetIndex];
        track.scrollTo({ left: targetOffset, behavior: 'smooth' });
        setActivePage(targetIndex);
    };

    const rebuildPages = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const positions = cards.map((card) => Math.min(maxScroll, card.offsetLeft));
        const uniquePositions = [];
        positions.forEach((value) => {
            const rounded = Math.round(value);
            if (!uniquePositions.includes(rounded)) uniquePositions.push(rounded);
        });
        pageOffsets = uniquePositions.length ? uniquePositions : [0];

        dotClickHandlers.forEach(({ dot, handler }) => {
            dot.removeEventListener('click', handler);
        });
        dotClickHandlers = [];

        dots.forEach((dot, idx) => {
            const onClick = () => scrollToPage(idx);
            dot.addEventListener('click', onClick);
            dotClickHandlers.push({ dot, handler: onClick });
        });

        const closestPage = pageOffsets.reduce((closestIdx, offset, idx) => {
            const currentDistance = Math.abs(offset - track.scrollLeft);
            const closestDistance = Math.abs(pageOffsets[closestIdx] - track.scrollLeft);
            return currentDistance < closestDistance ? idx : closestIdx;
        }, 0);
        setActivePage(closestPage);
    };

    addListener(prevBtn, 'click', () => {
        scrollToPage(activePageIndex - 1);
    });
    addListener(nextBtn, 'click', () => {
        scrollToPage(activePageIndex + 1);
    });

    const onTrackScroll = throttle(() => {
        const closestPage = pageOffsets.reduce((closestIdx, offset, idx) => {
            const currentDistance = Math.abs(offset - track.scrollLeft);
            const closestDistance = Math.abs(pageOffsets[closestIdx] - track.scrollLeft);
            return currentDistance < closestDistance ? idx : closestIdx;
        }, 0);
        setActivePage(closestPage);
    }, 120);
    addListener(track, 'scroll', onTrackScroll);

    const onResize = throttle(() => {
        rebuildPages();
    }, 180);
    addListener(window, 'resize', onResize);

    rebuildPages();

    destroyFleetCarousel = () => {
        dotClickHandlers.forEach(({ dot, handler }) => {
            dot.removeEventListener('click', handler);
        });
        dotClickHandlers = [];
        listeners.forEach((remove) => remove());
    };
}

function initCartShowcase() {
    const sections = document.querySelectorAll('[data-cart-showcase]');
    if (!sections.length) return;

    sections.forEach((section) => {
        const cards = Array.from(section.querySelectorAll('.cart-hero'));
        const options = Array.from(section.querySelectorAll('.cart-showcase__option'));
        const backdrop = section.querySelector('[data-cart-showcase-bg]');
        if (!cards.length) return;

        let activeId = cards[0].dataset.cartHero;

        const updateBackdrop = (id) => {
            if (!backdrop) return;
            const activeCard = cards.find((card) => card.dataset.cartHero === id) || cards[0];
            if (!activeCard) return;
            let bg = activeCard.dataset.bg;
            if (!bg) {
                const img = activeCard.querySelector('.cart-hero__image');
                bg = img?.currentSrc || img?.src || '';
            }
            if (bg) {
                backdrop.style.backgroundImage = `url('${bg}')`;
            }
        };

        const setActive = (id, { force = false } = {}) => {
            if (!id || (!force && id === activeId)) return;
            activeId = id;
            cards.forEach((card) => {
                card.classList.toggle('is-active', card.dataset.cartHero === id);
            });
            options.forEach((option) => {
                const isActive = option.dataset.cartOption === id;
                option.classList.toggle('is-active', isActive);
                option.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
            updateBackdrop(id);
        };

        options.forEach((option) => {
            option.addEventListener('click', () => setActive(option.dataset.cartOption));
        });

        updateBackdrop(activeId);
    });
}

function initProductLineup() {
    const showcases = document.querySelectorAll('[data-lineup]');
    if (!showcases.length) return;

    showcases.forEach((showcase) => {
        const cards = Array.from(showcase.querySelectorAll('[data-lineup-item]'));
        const triggers = Array.from(showcase.querySelectorAll('[data-lineup-trigger]'));
        if (!cards.length || !triggers.length) return;

        let activeId = cards[0].dataset.lineupItem;
        const getBackground = (id) => {
            const activeCard = cards.find((card) => card.dataset.lineupItem === id);
            const activeTrigger = triggers.find((trigger) => trigger.dataset.lineupTrigger === id);
            return activeCard?.dataset.lineupBg || activeTrigger?.dataset.lineupBg || '';
        };

        const applyBackground = (id) => {
            const bg = getBackground(id);
            if (bg) {
                showcase.style.setProperty('--lineup-bg', `url('${bg}')`);
            } else {
                showcase.style.removeProperty('--lineup-bg');
            }
        };

        const setupGallery = (card) => {
            const images = Array.from(card.querySelectorAll('[data-lineup-gallery-item]'));
            const thumbs = Array.from(card.querySelectorAll('[data-lineup-gallery-trigger]'));
            if (!images.length) return;
            let currentId = images[0].dataset.lineupGalleryItem;

            const setGalleryImage = (id) => {
                if (!id || currentId === id) return;
                currentId = id;
                images.forEach((img) => {
                    img.classList.toggle('is-active', img.dataset.lineupGalleryItem === id);
                });
                thumbs.forEach((thumb) => {
                    thumb.classList.toggle('is-active', thumb.dataset.lineupGalleryTrigger === id);
                });
            };

            thumbs.forEach((thumb) => {
                thumb.addEventListener('click', () => setGalleryImage(thumb.dataset.lineupGalleryTrigger));
            });
        };

        const setupDescriptionToggle = (card) => {
            const toggles = Array.from(card.querySelectorAll('[data-lineup-desc-toggle]'));
            toggles.forEach((toggle) => {
                const targetId = toggle.dataset.lineupDescToggle;
                const preview = card.querySelector(`[data-lineup-desc-preview="${targetId}"]`);
                const full = card.querySelector(`[data-lineup-desc-full="${targetId}"]`);
                if (!preview || !full) return;
                toggle.addEventListener('click', () => {
                    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
                    const nextState = !isExpanded;
                    const openLabel = toggle.dataset.lineupLabelOpen || 'View full description';
                    const closeLabel = toggle.dataset.lineupLabelClose || 'View less';
                    toggle.setAttribute('aria-expanded', nextState ? 'true' : 'false');
                    preview.hidden = nextState;
                    full.hidden = !nextState;
                    toggle.textContent = nextState ? closeLabel : openLabel;
                });
            });
        };

        cards.forEach((card) => setupGallery(card));
        cards.forEach((card) => setupDescriptionToggle(card));
        const setupZoom = (card) => {
            const zoomBtn = card.querySelector('[data-lineup-zoom]');
            const modal = card.querySelector('[data-lineup-zoom-modal]');
            const modalImg = modal?.querySelector('[data-lineup-zoom-image]');
            if (!zoomBtn || !modal || !modalImg) return;

            const closeModal = () => {
                modal.classList.remove('is-open');
                modal.setAttribute('hidden', '');
                document.body.classList.remove('gcr-lineup--zoom-open');
            };

            const openModal = () => {
                const activeImage = card.querySelector('.gcr-lineup__slide.is-active img');
                if (activeImage) {
                    const source =
                        activeImage.dataset.lineupZoomSrc ||
                        activeImage.currentSrc ||
                        activeImage.src;
                    if (source) {
                        modalImg.src = source;
                    }
                    modalImg.alt = activeImage.alt || modalImg.alt || 'Vista ampliada';
                }
                modal.classList.add('is-open');
                modal.removeAttribute('hidden');
                document.body.classList.add('gcr-lineup--zoom-open');
            };

            zoomBtn.addEventListener('click', openModal);
            modal.addEventListener('click', (event) => {
                if (event.target === modal) {
                    closeModal();
                }
            });
            const closeTriggers = Array.from(modal.querySelectorAll('[data-lineup-zoom-close]'));
            closeTriggers.forEach((btn) => btn.addEventListener('click', closeModal));
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape' && modal.classList.contains('is-open')) {
                    closeModal();
                }
            });
        };
        cards.forEach((card) => setupZoom(card));

        const setActive = (id) => {
            if (!id) return;
            applyBackground(id);
            if (id === activeId) return;
            activeId = id;
            cards.forEach((card) => {
                card.classList.toggle('is-active', card.dataset.lineupItem === id);
            });
            triggers.forEach((trigger) => {
                const isActive = trigger.dataset.lineupTrigger === id;
                trigger.classList.toggle('is-active', isActive);
                trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
        };

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => setActive(trigger.dataset.lineupTrigger));
        });

        applyBackground(activeId);
    });
}

// Coverage map draw-on-scroll animation
function initCoverageMap() {
    const mapFigure = document.querySelector('.coverage__map');
    if (!mapFigure) return;

    const paths = Array.from(mapFigure.querySelectorAll('path.st0, .st0'));
    const prefersReduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduceMotion) {
        paths.forEach((path) => {
            try {
                const length = Math.ceil(path.getTotalLength());
                path.style.strokeDasharray = `${length}`;
                path.style.strokeDashoffset = `${length}`;
                path.style.setProperty('--dash', `${length}`);
            } catch (error) {
                // Element might not support getTotalLength
            }
        });
    } else {
        paths.forEach((path) => {
            path.style.strokeDasharray = 'none';
            path.style.strokeDashoffset = 0;
        });
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                mapFigure.classList.add('is-visible');
                obs.unobserve(entry.target);
            }
        });
    }, { threshold: 0.4 });

    observer.observe(mapFigure);
}

// Scroll Effects and Animations
function initScrollEffects() {
    // Intersection Observer for fade-in animations
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);
    
    // Observe elements for animation
    const animateElements = document.querySelectorAll('.service-card, .testimonial-card, .value-item, .section-header');
    animateElements.forEach(el => {
        observer.observe(el);
    });
    
    // Parallax effect for hero section
    window.addEventListener('scroll', function() {
        const scrolled = window.pageYOffset;
        const hero = document.querySelector('.hero');
        const heroImage = document.querySelector('.hero-image');
        
        if (hero && heroImage) {
            const rate = scrolled * -0.5;
            heroImage.style.transform = `translateY(${rate}px)`;
        }
    });
}

// Form Handling
function initFormHandling() {
    const contactForm = document.querySelector('.contact-form form');
    
    if (contactForm) {
        contactForm.addEventListener('submit', function(e) {
            e.preventDefault();
            
            // Get form data
            const formData = new FormData(contactForm);
            const formObject = {};
            
            // Convert FormData to object
            for (let [key, value] of formData.entries()) {
                formObject[key] = value;
            }
            
            // Validate form
            if (validateForm(formObject)) {
                // Show success message
                showNotification('¡Gracias por tu consulta! Te contactaremos pronto.', 'success');
                
                // Reset form
                contactForm.reset();
                
                // Add loading state to button
                const submitBtn = contactForm.querySelector('button[type="submit"]');
                const originalText = submitBtn.textContent;
                submitBtn.textContent = 'Enviando...';
                submitBtn.disabled = true;
                
                // Simulate form submission
                setTimeout(() => {
                    submitBtn.textContent = originalText;
                    submitBtn.disabled = false;
                }, 2000);
            }
        });
        
        // Real-time validation
        const inputs = contactForm.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            input.addEventListener('blur', function() {
                validateField(this);
            });
            
            input.addEventListener('input', function() {
                if (this.classList.contains('error')) {
                    validateField(this);
                }
            });
        });
    }
}

// Form Validation
function validateForm(data) {
    let isValid = true;
    const form = document.querySelector('.contact-form form');
    if (!form) return true;
    
    // Clear previous errors
    form.querySelectorAll('.error').forEach(el => {
        el.classList.remove('error');
    });
    
    // Validate required fields
    const requiredFields = form.querySelectorAll('[required]');
    requiredFields.forEach(field => {
        if (!field.value.trim()) {
            field.classList.add('error');
            isValid = false;
        }
    });
    
    // Validate email
    const emailField = form.querySelector('input[type="email"]');
    if (emailField && emailField.value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailField.value)) {
            emailField.classList.add('error');
            isValid = false;
        }
    }
    
    if (!isValid) {
        showNotification('Por favor, completa todos los campos requeridos correctamente.', 'error');
    }
    
    return isValid;
}

function validateField(field) {
    field.classList.remove('error');
    
    if (field.hasAttribute('required') && !field.value.trim()) {
        field.classList.add('error');
        return false;
    }
    
    if (field.type === 'email' && field.value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(field.value)) {
            field.classList.add('error');
            return false;
        }
    }
    
    return true;
}

// Notification System
function showNotification(message, type = 'info') {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(notification => {
        notification.remove();
    });
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.innerHTML = `
        <div class="notification-content">
            <span class="notification-message">${message}</span>
            <button class="notification-close">&times;</button>
        </div>
    `;
    
    // Add styles
    notification.style.cssText = `
        position: fixed;
        top: 100px;
        right: 20px;
        background: ${type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#2196F3'};
        color: white;
        padding: 1rem 1.5rem;
        border-radius: 4px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        transform: translateX(100%);
        transition: transform 0.3s ease;
        max-width: 400px;
    `;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
        notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Close functionality
    const closeBtn = notification.querySelector('.notification-close');
    closeBtn.addEventListener('click', () => {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
            notification.remove();
        }, 300);
    });
    
    // Auto remove after 5 seconds
    setTimeout(() => {
        if (notification.parentNode) {
            notification.style.transform = 'translateX(100%)';
            setTimeout(() => {
                notification.remove();
            }, 300);
        }
    }, 5000);
}

// Smooth Scrolling for Navigation Links
function initSmoothScrolling() {
    const navLinks = document.querySelectorAll('a[href^="#"], a[href^="/"][href*="#"]');

    const closeSideMenuIfOpen = () => {
        const sideMenu = document.getElementById('sidemenu');
        if (!sideMenu || sideMenu.getAttribute('aria-hidden') !== 'false') return;
        const closeTrigger = sideMenu.querySelector('[data-menu-close]');
        if (closeTrigger) {
            closeTrigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            return;
        }
        sideMenu.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('side-menu-open');
        document.body.style.overflow = '';
        const headerToggle = document.querySelector('.rr-header__menu');
        headerToggle?.setAttribute('aria-expanded', 'false');
    };

    const getTargetFromHash = (hash) => {
        if (!hash || hash === '#') return null;
        const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
        if (!rawId) return null;
        let id = rawId;
        try {
            id = decodeURIComponent(rawId);
        } catch (_) {}
        return document.getElementById(id);
    };

    const scrollToTarget = (target) => {
        if (!target) return;
        closeSideMenuIfOpen();
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset;
        const offsetTop = targetTop - 80; // Account for fixed navbar
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    };
    
    navLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            const href = this.getAttribute('href');
            if (!href) return;

            if (href.startsWith('#')) {
                e.preventDefault();
                const targetSection = getTargetFromHash(href);
                if (targetSection) scrollToTarget(targetSection);
                return;
            }

            let url;
            try {
                url = new URL(href, window.location.href);
            } catch (_) {
                return;
            }

            if (!url.hash) return;
            if (url.origin !== window.location.origin) return;
            if (url.pathname !== window.location.pathname) return;

            const targetSection = getTargetFromHash(url.hash);
            if (!targetSection) return;
            e.preventDefault();
            history.pushState(null, '', url.hash);
            scrollToTarget(targetSection);
        });
    });
}

// Initialize Animations
function initAnimations() {
    // Add stagger animation to service cards
    const serviceCards = document.querySelectorAll('.service-card');
    serviceCards.forEach((card, index) => {
        card.style.animationDelay = `${index * 0.1}s`;
    });
    
    // Add stagger animation to testimonial cards
    const testimonialCards = document.querySelectorAll('.testimonial-card');
    testimonialCards.forEach((card, index) => {
        card.style.animationDelay = `${index * 0.15}s`;
    });
    
    // Add hover effects to buttons
    const buttons = document.querySelectorAll('.btn-primary, .btn-secondary');
    buttons.forEach(button => {
        button.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-2px)';
        });
        
        button.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0)';
        });
    });
}

// Utility Functions
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

function throttle(func, limit) {
    let inThrottle;
    return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    }
}

// Performance optimized scroll handler
const optimizedScrollHandler = throttle(function() {
    // Handle scroll-based animations here if needed
}, 16); // ~60fps

window.addEventListener('scroll', optimizedScrollHandler);

// Lazy loading for images (if any are added later)
function initLazyLoading() {
    const images = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                img.src = img.dataset.src;
                img.classList.remove('lazy');
                imageObserver.unobserve(img);
            }
        });
    });
    
    images.forEach(img => imageObserver.observe(img));
}

// Add CSS for error states and animations
const additionalStyles = `
    .error {
        border-color: #f44336 !important;
        background-color: rgba(244, 67, 54, 0.1) !important;
    }
    
    .notification-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
    }
    
    .notification-close {
        background: none;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .fade-in-up {
        animation: fadeInUp 0.8s ease-out forwards;
    }
    
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

// Inject additional styles
const styleSheet = document.createElement('style');
styleSheet.textContent = additionalStyles;
document.head.appendChild(styleSheet);

})();
